// Trending row that adapts to viewport:
//   - mobile/sm: horizontal snap-scroll, fixed card widths (140–200px)
//   - lg+: 5 cards exactly fill the container, horizontal-scroll past 5 to
//     reveal items 6–N. Floating left/right chevrons overlay the card
//     edges and scroll-by-page on click — matches the mockup's "more
//     available" affordance.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../cn.ts";

export interface TrendingRowProps {
  title: string;
  seeAllHref?: string;
  seeAllLabel?: string;
  children: React.ReactNode;
  /** Heading color. Defaults to white because the trending row sits on the dark band. */
  headingClassName?: string;
  /** Number of cards visible on lg+ (controls per-card width). Defaults to 5. */
  desktopVisibleCount?: 4 | 5 | 6;
  /** Hide the floating chevron overlay. */
  hideChevrons?: boolean;
  className?: string;
}

const VISIBLE_WIDTH_CLASSES = {
  4: "lg:[&>*]:!w-[calc((100%-3rem)/4)] lg:[&>*]:!max-w-none lg:[&>*]:!min-w-0",
  5: "lg:[&>*]:!w-[calc((100%-4rem)/5)] lg:[&>*]:!max-w-none lg:[&>*]:!min-w-0",
  6: "lg:[&>*]:!w-[calc((100%-5rem)/6)] lg:[&>*]:!max-w-none lg:[&>*]:!min-w-0",
} as const;

export function TrendingRow({
  title,
  seeAllHref,
  seeAllLabel,
  children,
  headingClassName,
  desktopVisibleCount = 5,
  hideChevrons,
  className,
}: TrendingRowProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, [updateEdges]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * direction, behavior: "smooth" });
  };

  return (
    <section className={cn("space-y-4", className)}>
      <header className="flex items-baseline justify-between px-4 sm:px-0">
        <h2
          className={cn("font-heading text-2xl font-bold text-white sm:text-3xl", headingClassName)}
        >
          {title}
        </h2>
        {seeAllHref ? (
          <a
            href={seeAllHref}
            className="text-sm font-semibold text-[var(--brand-rose)] hover:underline"
          >
            {seeAllLabel ?? "See all"}
          </a>
        ) : null}
      </header>

      <div className="relative">
        <div
          ref={scrollerRef}
          className={cn(
            // Universal: flex with horizontal snap-scroll
            "scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 sm:px-0",
            // Mobile / sm: fixed card widths so 2–3 are visible at once
            "[&>*]:w-[42vw] [&>*]:min-w-[140px] [&>*]:max-w-[180px] [&>*]:snap-start [&>*]:shrink-0",
            "sm:[&>*]:w-[160px] sm:[&>*]:min-w-[160px] sm:[&>*]:max-w-[200px]",
            // lg+: each card = container/N minus gap, so N cards exactly fill the row
            "lg:gap-4 lg:px-0",
            VISIBLE_WIDTH_CLASSES[desktopVisibleCount],
          )}
        >
          {children}
        </div>

        {!hideChevrons ? (
          <>
            <button
              type="button"
              onClick={() => scrollByPage(-1)}
              aria-label="Previous"
              aria-disabled={atStart}
              className={cn(
                "absolute left-0 top-1/2 hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full lg:flex",
                "bg-white text-[var(--brand-ink)] shadow-xl ring-1 ring-black/5",
                "transition-all hover:scale-105",
                atStart ? "pointer-events-none opacity-0" : "opacity-100",
              )}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollByPage(1)}
              aria-label="Next"
              aria-disabled={atEnd}
              className={cn(
                "absolute right-0 top-1/2 hidden h-10 w-10 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full lg:flex",
                "bg-white text-[var(--brand-ink)] shadow-xl ring-1 ring-black/5",
                "transition-all hover:scale-105",
                atEnd ? "pointer-events-none opacity-0" : "opacity-100",
              )}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}
